import { Box } from "@mui/material";
import { useEffect, useState } from "react";

// title-blink-fast.gif is a 4s loop where the sparkle is only visible during the
// first ~1.2s. Browsers share a single animation clock between every <img>
// that points to the same URL, so each light needs its own URL to twinkle
// independently. We then mount the lights staggered across the loop, which
// keeps ~2 of them lit at any moment (christmas tree effect).
const GIF_LOOP_MS = 4000;

// order in `lightsOrder` defines the lighting sequence, spread out so
// consecutive lights are never next to each other
const lights = [
	{ top: "2%", left: "33%", width: "12%" },
	{ top: "5%", left: "47%", width: "10%" },
	{ top: "9%", left: "61%", width: "12%" },
	{ top: "31%", left: "7%", width: "13%" },
	{ top: "32%", left: "27%", width: "11%" },
	{ top: "33%", left: "52%", width: "12%" },
	{ top: "34%", left: "75%", width: "11%" }
] as const;
const lightsOrder = [0, 5, 3, 1, 6, 4, 2];

// shrinks every light around its own center (keeps positions), the gif loses
// quality when rendered too big
const LIGHT_SCALE = 0.75;

const prefersReducedMotion = () =>
	window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

const loadImage = (src: string) =>
	new Promise<void>((resolve) => {
		const image = new Image();
		image.onload = () => resolve();
		image.onerror = () => resolve();
		image.src = src;
	});

// One distinct URL per light. Preferred: download the gif once and create an
// object URL per light. If the CDN doesn't allow the fetch (no CORS headers),
// fall back to a query param per light (each one downloaded once, then cached).
const createLightUrls = async (src: string, count: number) => {
	try {
		const response = await fetch(src);
		if (!response.ok) {
			throw new Error(`${response.status}`);
		}
		const blob = await response.blob();
		const urls = Array.from({ length: count }, () => URL.createObjectURL(blob));
		return { urls, revoke: () => urls.forEach(URL.revokeObjectURL) };
	} catch {
		const separator = src.includes("?") ? "&" : "?";
		const urls = Array.from(
			{ length: count },
			(_, i) => `${src}${separator}light=${i}`
		);
		return { urls, revoke: () => undefined };
	}
};

const TitleSparkles = ({ src }: { src: string }) => {
	const [lightUrls, setLightUrls] = useState<(string | null)[]>(() =>
		lights.map(() => null)
	);

	useEffect(() => {
		if (prefersReducedMotion()) {
			return;
		}

		let cancelled = false;
		let revoke: () => void = () => undefined;
		const timers: number[] = [];

		createLightUrls(src, lights.length).then(async (result) => {
			revoke = result.revoke;
			if (cancelled) {
				revoke();
				return;
			}
			// wait until every light is decoded so the stagger isn't skewed by
			// network time
			await Promise.all(result.urls.map(loadImage));
			if (cancelled) {
				return;
			}
			const step = GIF_LOOP_MS / lights.length;
			lightsOrder.forEach((lightIndex, position) => {
				timers.push(
					window.setTimeout(() => {
						setLightUrls((prev) => {
							const next = [...prev];
							next[lightIndex] = result.urls[lightIndex];
							return next;
						});
					}, position * step)
				);
			});
		});

		return () => {
			cancelled = true;
			timers.forEach(clearTimeout);
			revoke();
		};
	}, [src]);

	return (
		<>
			{lights.map((light, index) => {
				const url = lightUrls[index];
				return url ? (
					<Box
						key={index}
						component="img"
						src={url}
						alt=""
						aria-hidden="true"
						draggable={false}
						sx={{
							position: "absolute",
							top: light.top,
							left: light.left,
							width: light.width,
							transform: `scale(${LIGHT_SCALE})`,
							pointerEvents: "none",
							userSelect: "none"
						}}
					/>
				) : null;
			})}
		</>
	);
};

export default TitleSparkles;
